import { World } from '../sim/world'
import { Rarity, RARITY_WEIGHT, RARITY_ORDER } from '../sim/types'
import { POOL_WEAPONS, WEAPONS } from './weapons'
import { ITEM_LIST, ITEMS } from './items'
import { TOME_LIST, TOMES } from './tomes'
import { healPlayer } from '../sim/combat'
import { t, tName, tDesc } from '../i18n'

export const MAX_WEAPONS = 5
export const MAX_ITEMS = 6
export const MAX_TOMES = 5 // tomes are now capped like weapons — a focused build, not an endless stack of stat books

export type UpgradeKind =
  | 'weapon-new'
  | 'weapon-up'
  | 'item-new'
  | 'item-up'
  | 'tome-new'
  | 'tome-up'
  | 'evolve'
  | 'heal'
  | 'gold'

export interface UpgradeOption {
  kind: UpgradeKind
  id: string
  name: string
  icon: string
  rarity: Rarity
  desc: string
  tag: string // short label e.g. "NEW", "LV 3", "EVOLVE"
  apply: (world: World) => void
}

/** Stable identity of a draft card, used for lock/banish across re-draws. */
export function optionKey(o: UpgradeOption): string {
  return `${o.kind}:${o.id}`
}
export function isBanishable(o: UpgradeOption): boolean {
  return o.kind !== 'evolve' && o.kind !== 'heal' && o.kind !== 'gold'
}
export function isLockable(o: UpgradeOption): boolean {
  return o.kind !== 'heal' && o.kind !== 'gold'
}

/** Where a set of options is being offered (MP5 — per-source loot identity). */
export type LootSource = 'draft' | 'vendor' | 'chest' | 'boss'

/**
 * Which options a given source may offer. Evolutions are DRAFT-ONLY (they come from leveling a
 * weapon to max while holding its catalyst — never bought or looted), giving the level-up draft a
 * signature the vendor/chest/boss pools don't share.
 */
export function sourceAllows(o: UpgradeOption, source: LootSource): boolean {
  if (source === 'draft') return true
  // Weapons AND tomes are acquired ONLY through the level-up draft — the vendor, chests and boss rewards give passive
  // ITEMS (+ gold/heal). This keeps each source's role clear: level-up = your weapon/tome build; loot = items + economy.
  if (o.kind === 'weapon-new' || o.kind === 'weapon-up' || o.kind === 'tome-new' || o.kind === 'tome-up') return false
  return o.kind !== 'evolve' // evolutions are draft-only too
}

function rarityWeight(r: Rarity, luck: number): number {
  let w = RARITY_WEIGHT[r]
  if (r === 'rare') w *= 1 + luck * 0.12
  if (r === 'epic') w *= 1 + luck * 0.2
  if (r === 'legendary') w *= 1 + luck * 0.3
  return Math.max(0.01, w)
}

export function buildCandidates(world: World): UpgradeOption[] {
  const out: UpgradeOption[] = []

  // --- evolutions (high value, surfaced first) ---
  for (const ws of world.weapons) {
    const def = WEAPONS[ws.defId]
    if (!def || ws.evolved || !def.evolvesTo || !def.evolveItem) continue
    const evoTarget = WEAPONS[def.evolvesTo]
    const hasItem = world.hasItem(def.evolveItem)
    if (ws.level >= def.maxLevel && hasItem && evoTarget) {
      out.push({
        kind: 'evolve',
        id: def.id,
        name: tName('weapon', def.evolvesTo, evoTarget.name),
        icon: evoTarget.icon,
        rarity: 'legendary',
        tag: t('tag_evolve'),
        desc: tDesc('weapon', def.evolvesTo, 1, evoTarget.describe),
        apply: (w) => {
          const target = w.hasWeapon(def.id)
          if (target) {
            // retire the source weapon's persistent orbital orbs (life≈1e9) before swapping — otherwise an
            // evolution that doesn't use refreshOrbitals (e.g. Plasma Cutter→Vortex) leaves them spinning forever
            if (target.orbitalIds) {
              for (const id of target.orbitalIds) {
                const orb = w.projectiles.find((p) => p.id === id)
                if (orb) orb.alive = false
              }
              target.orbitalIds = []
            }
            // carry invested levels into the evolution (don't strand them at 1)
            const prevLevel = target.level
            target.defId = def.evolvesTo!
            target.evolved = true
            target.level = Math.min(evoTarget.maxLevel, Math.max(1, prevLevel - 2))
            target.cooldown = 0
            target.scratch = {}
          }
        },
      })
    }
  }

  // --- weapon upgrades (incl. owned evolved weapons, which now scale) ---
  for (const ws of world.weapons) {
    const def = WEAPONS[ws.defId]
    if (!def) continue
    if (ws.level < def.maxLevel) {
      out.push({
        kind: 'weapon-up',
        id: def.id,
        name: tName('weapon', def.id, def.name),
        icon: def.icon,
        rarity: def.rarity,
        tag: `LV ${ws.level + 1}`,
        desc: tDesc('weapon', def.id, ws.level + 1, def.describe),
        apply: (w) => {
          const target = w.hasWeapon(def.id)
          if (target) target.level++
        },
      })
    }
  }

  // --- new weapons ---
  if (world.weapons.length < MAX_WEAPONS) {
    for (const def of POOL_WEAPONS) {
      if (world.hasWeapon(def.id)) continue
      if (world.disabledItems.has(def.id)) continue // player toggled this weapon off (anti-dilution; ids are globally unique)
      out.push({
        kind: 'weapon-new',
        id: def.id,
        name: tName('weapon', def.id, def.name),
        icon: def.icon,
        rarity: def.rarity,
        tag: t('tag_new'),
        desc: tDesc('weapon', def.id, 1, def.describe),
        apply: (w) => {
          if (w.weapons.length >= MAX_WEAPONS) return // hard cap (the offer gate can be bypassed by multi-buy in the vendor)
          w.weapons.push({ defId: def.id, level: 1, cooldown: 0, evolved: false, scratch: {} })
        },
      })
    }
  }

  // --- item upgrades ---
  for (const it of world.items) {
    const def = ITEMS[it.defId]
    if (!def) continue
    if (it.level < def.maxLevel) {
      out.push({
        kind: 'item-up',
        id: def.id,
        name: tName('item', def.id, def.name),
        icon: def.icon,
        rarity: def.rarity,
        tag: `LV ${it.level + 1}`,
        desc: tDesc('item', def.id, it.level + 1, def.describe),
        apply: (w) => {
          const target = w.hasItem(def.id)
          if (target) target.level++
        },
      })
    }
  }

  // --- new items ---
  if (world.items.length < MAX_ITEMS) {
    for (const def of ITEM_LIST) {
      if (world.hasItem(def.id)) continue
      if (world.disabledItems.has(def.id)) continue // player toggled this item off
      out.push({
        kind: 'item-new',
        id: def.id,
        name: tName('item', def.id, def.name),
        icon: def.icon,
        rarity: def.rarity,
        tag: t('tag_new'),
        desc: tDesc('item', def.id, 1, def.describe),
        apply: (w) => {
          if (w.items.length >= MAX_ITEMS) return // hard cap (the offer gate can be bypassed by multi-buy in the vendor)
          w.items.push({ defId: def.id, level: 1 })
        },
      })
    }
  }

  // --- tome upgrades (stackable stat books) ---
  for (const tm of world.tomes) {
    const def = TOMES[tm.defId]
    if (def && tm.level < def.maxLevel) {
      out.push({
        kind: 'tome-up',
        id: def.id,
        name: tName('tome', def.id, def.name),
        icon: def.icon,
        rarity: def.rarity,
        tag: `LV ${tm.level + 1}`,
        desc: tDesc('tome', def.id, tm.level + 1, def.describe),
        apply: (w) => {
          const target = w.hasTome(def.id)
          if (target) target.level++
        },
      })
    }
  }
  // --- new tomes (capped: a focused set of stat books, no longer an endless stack) ---
  if (world.tomes.length < MAX_TOMES) {
    for (const def of TOME_LIST) {
      if (world.hasTome(def.id)) continue
      if (world.disabledItems.has(def.id)) continue // player toggled this tome off (anti-dilution; ids are globally unique)
      out.push({
        kind: 'tome-new',
        id: def.id,
        name: tName('tome', def.id, def.name),
        icon: def.icon,
        rarity: def.rarity,
        tag: t('tag_new'), // the category ribbon now says TOMO, so the corner tag is just NEW (was a redundant "TOMO TOMO")
        desc: tDesc('tome', def.id, 1, def.describe),
        apply: (w) => {
          if (w.tomes.length >= MAX_TOMES) return // hard cap (the offer gate can be bypassed by multi-buy in the vendor)
          w.tomes.push({ defId: def.id, level: 1 })
        },
      })
    }
  }

  // remove anything banished this run (evolutions are never banishable)
  return out.filter((o) => !(isBanishable(o) && world.banished.has(o.id)))
}

/** Fallback options when the build is full / everything maxed. */
function fallbackOptions(): UpgradeOption[] {
  return [
    {
      kind: 'heal',
      id: 'heal',
      name: t('feast'),
      icon: '🍗',
      rarity: 'common',
      tag: t('tag_heal'),
      desc: t('feastDesc'),
      apply: (w) => healPlayer(w, w.stats.maxHp * 0.4),
    },
    {
      kind: 'gold',
      id: 'gold',
      name: t('coinStash'),
      icon: '💰',
      rarity: 'uncommon',
      tag: t('tag_gold'),
      desc: t('coinStashDesc'),
      apply: (w) => {
        w.gainGold(50)
      },
    },
  ]
}

/**
 * Pick `count` distinct upgrade options for a level-up screen, weighted by
 * rarity and the player's luck. Evolutions are always surfaced when available.
 */
export function generateOptions(world: World, count = 3, source: LootSource = 'draft'): UpgradeOption[] {
  const candidates = buildCandidates(world)

  // BOSS — one big unique: the single rarest non-evolve reward available (tie-broken by seeded rng).
  if (source === 'boss') {
    const pool = candidates.filter((c) => sourceAllows(c, 'boss'))
    if (pool.length === 0) return fallbackOptions().slice(0, count)
    const maxRank = Math.max(...pool.map((c) => RARITY_ORDER.indexOf(c.rarity)))
    const top = pool.filter((c) => RARITY_ORDER.indexOf(c.rarity) === maxRank)
    return [top[world.rng.int(0, top.length - 1)]]
  }

  const chosen: UpgradeOption[] = []
  const used = new Set<string>()

  // a locked card (if still valid) is guaranteed to reappear — draft only
  if (source === 'draft' && world.lockedKey) {
    const locked = candidates.find((c) => optionKey(c) === world.lockedKey)
    if (locked) {
      chosen.push(locked)
      used.add(world.lockedKey)
    } else {
      world.lockedKey = null // lock became stale (e.g. weapon maxed)
    }
  }

  // evolutions get guaranteed slots — draft only (they're never sold or looted)
  if (source === 'draft') {
    for (const e of candidates) {
      if (e.kind === 'evolve' && !used.has(optionKey(e)) && chosen.length < count) {
        chosen.push(e)
        used.add(optionKey(e))
      }
    }
  }

  const pool = candidates.filter((c) => c.kind !== 'evolve' && !used.has(optionKey(c)) && sourceAllows(c, source))
  // curse feeds loot quality: each curse point raises rare/epic/legendary odds.
  // the vendor is a curated premium stall → an extra luck bias on top.
  const luck = world.stats.luck + world.curse * 0.15 + (source === 'vendor' ? 2 : 0)

  while (chosen.length < count && pool.length > 0) {
    const weights = pool.map((c) => rarityWeight(c.rarity, luck))
    const idx = world.rng.weightedIndex(weights)
    chosen.push(pool[idx])
    used.add(optionKey(pool[idx]))
    pool.splice(idx, 1)
  }

  // pad with fallbacks if needed
  if (chosen.length < count) {
    for (const f of fallbackOptions()) {
      if (chosen.length < count) chosen.push(f)
    }
  }

  return chosen
}
